/**
 * Admin API
 */
import api from './index'

export interface AdminStats {
  companies: {
    total: number
    by_tier: Record<string, number>
    by_status: Record<string, number>
  }
  client_users: {
    total: number
    active: number
  }
  invitations: {
    pending: number
  }
  team: {
    total: number
    active: number
  }
  deals: {
    total: number
    open: number
  }
  projects: {
    total: number
    active: number
  }
  invoices: {
    outstanding: number
  }
}

export interface AuditLogEntry {
  id: string
  action: 'create' | 'update' | 'delete' | 'login' | 'export' | string
  entity_type: string
  entity_id?: string
  entity_name?: string
  user_id: string
  user_name?: string
  user_type: 'team' | 'client'
  details?: Record<string, unknown>
  ip_address?: string
  user_agent?: string
  created_at: string
}

export interface AuditLogFilters {
  entity_type?: string
  action?: string
  user_id?: string
  start_date?: string
  end_date?: string
  limit?: number
  cursor?: string
}

export interface SystemSettings {
  id?: string
  company_name: string
  company_logo?: string
  company_address: string
  company_phone: string
  company_email: string
  company_website: string
  default_currency: string
  default_timezone: string
  default_payment_terms: number
  invoice_prefix: string
  proposal_prefix: string
  enable_client_portal: boolean
  require_approval_for_proposals: boolean
  allow_client_file_uploads: boolean
  email_notifications_enabled: boolean
  microsoft_integration_enabled: boolean
  updated_at?: string
  updated_by?: string
}

export interface SystemEmailTemplate {
  id: string
  name: string
  type: 'invitation' | 'proposal' | 'invoice' | 'reminder' | 'notification'
  subject: string
  body_html: string
  body_text?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

// Multi-tenant management types
export interface ManagedCompany {
  id: string
  name: string
  domain?: string
  industry?: string
  company_size?: string
  subscription_tier: 'basic' | 'professional' | 'enterprise'
  is_client: boolean
  is_active: boolean
  white_label_config?: {
    logo_url?: string
    primary_color?: string
    secondary_color?: string
    portal_subdomain?: string
  }
  created_at: string
  updated_at: string
}

export interface ManagedUser {
  id: string
  firebase_uid: string
  email: string
  display_name?: string
  photo_url?: string
  role: string
  company_id: string
  is_active: boolean
  last_login_at?: string
  created_at: string
  updated_at: string
}

export interface CreateCompanyData {
  name: string
  domain?: string
  industry?: string
  company_size?: string
  subscription_tier?: 'basic' | 'professional' | 'enterprise'
  white_label_config?: Record<string, string>
}

export interface CreateUserData {
  firebase_uid: string
  email: string
  display_name?: string
  photo_url?: string
  role: string
}

export interface MultiTenantStats {
  companies: {
    total: number
    active: number
    clients: number
  }
  users: {
    total: number
    active: number
  }
  data: {
    contacts: number
    deals: number
    total_deal_value: number
    won_deal_value: number
  }
}

export interface CompanyExport {
  export_date: string
  export_version: string
  company: ManagedCompany
  users?: ManagedUser[]
  contacts: unknown[]
  deals: unknown[]
  activities: unknown[]
  notes: unknown[]
  summary: {
    company_name: string
    contacts_count: number
    deals_count: number
    activities_count: number
    notes_count: number
    users_count: number
  }
}

export const adminApi = {
  /**
   * Get admin dashboard stats
   */
  async getStats(): Promise<AdminStats> {
    const { data } = await api.get('/admin/stats')
    return data.data
  },

  /**
   * Get audit log entries
   */
  async getAuditLog(filters?: AuditLogFilters): Promise<{ data: AuditLogEntry[]; has_more: boolean }> {
    const { data } = await api.get('/admin/audit-log', { params: filters })
    return data
  },

  /**
   * Create an audit log entry
   */
  async createAuditEntry(entry: {
    action: string
    entity_type: string
    entity_id?: string
    entity_name?: string
    details?: Record<string, unknown>
  }): Promise<AuditLogEntry> {
    const { data } = await api.post('/admin/audit-log', entry)
    return data.data
  },

  /**
   * Get system settings
   */
  async getSettings(): Promise<SystemSettings> {
    const { data } = await api.get('/admin/settings')
    return data.data
  },

  /**
   * Update system settings
   */
  async updateSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    const { data } = await api.put('/admin/settings', settings)
    return data.data
  },

  /**
   * Get email templates
   */
  async getEmailTemplates(): Promise<SystemEmailTemplate[]> {
    const { data } = await api.get('/admin/email-templates')
    return data.data
  },

  /**
   * Get a specific email template
   */
  async getEmailTemplate(templateId: string): Promise<SystemEmailTemplate> {
    const { data } = await api.get(`/admin/email-templates/${templateId}`)
    return data.data
  },

  /**
   * Update an email template
   */
  async updateEmailTemplate(
    templateId: string,
    updates: Partial<SystemEmailTemplate>
  ): Promise<SystemEmailTemplate> {
    const { data } = await api.put(`/admin/email-templates/${templateId}`, updates)
    return data.data
  },

  /**
   * Export data
   */
  async exportData(entityType: string): Promise<unknown[]> {
    const { data } = await api.get(`/admin/export/${entityType}`)
    return data.data
  },

  // ============================================
  // Multi-Tenant Company Management
  // ============================================

  /**
   * List all companies (admin view)
   */
  async listCompanies(filters?: {
    is_client?: boolean
    is_active?: boolean
  }): Promise<{ companies: ManagedCompany[]; total: number }> {
    const { data } = await api.get('/admin/companies', { params: filters })
    return data
  },

  /**
   * Create a new client company
   */
  async createCompany(companyData: CreateCompanyData): Promise<ManagedCompany> {
    const { data } = await api.post('/admin/companies', companyData)
    return data.company
  },

  /**
   * Update company details
   */
  async updateCompany(
    companyId: string,
    updates: Partial<CreateCompanyData & { is_active: boolean }>
  ): Promise<ManagedCompany> {
    const { data } = await api.put(`/admin/companies/${companyId}`, updates)
    return data.company
  },

  /**
   * Deactivate a company (soft delete)
   */
  async deactivateCompany(companyId: string): Promise<ManagedCompany> {
    const { data } = await api.post(`/admin/companies/${companyId}/deactivate`)
    return data.company
  },

  // ============================================
  // Multi-Tenant User Management
  // ============================================

  /**
   * List users for a company
   */
  async listCompanyUsers(companyId: string): Promise<{ users: ManagedUser[]; total: number }> {
    const { data } = await api.get(`/admin/companies/${companyId}/users`)
    return data
  },

  /**
   * Create a user for a company
   */
  async createCompanyUser(companyId: string, userData: CreateUserData): Promise<ManagedUser> {
    const { data } = await api.post(`/admin/companies/${companyId}/users`, userData)
    return data.user
  },

  /**
   * Deactivate a user
   */
  async deactivateUser(userId: string): Promise<ManagedUser> {
    const { data } = await api.post(`/admin/users/${userId}/deactivate`)
    return data.user
  },

  // ============================================
  // Multi-Tenant Statistics
  // ============================================

  /**
   * Get multi-tenant system statistics
   */
  async getMultiTenantStats(): Promise<MultiTenantStats> {
    const { data } = await api.get('/admin/stats')
    return data
  },

  // ============================================
  // Data Export/Import
  // ============================================

  /**
   * Export all data for a company
   */
  async exportCompanyData(
    companyId: string,
    includeUsers: boolean = true
  ): Promise<CompanyExport> {
    const { data } = await api.post(
      `/admin/companies/${companyId}/export`,
      null,
      { params: { include_users: includeUsers } }
    )
    return data
  },

  /**
   * Import company data from JSON
   */
  async importCompanyData(
    importData: CompanyExport,
    preserveIds: boolean = true
  ): Promise<{ company_id: string; message: string }> {
    const { data } = await api.post(
      '/admin/companies/import',
      importData,
      { params: { preserve_ids: preserveIds } }
    )
    return data
  },
}

export default adminApi
